/**
 * Org subdomain host resolution (EPIC-007 / CONTRACT org-host-resolution).
 * Cite CONTRACT for apex env table — do not redefine hosts here.
 */

const SSO_LABEL_PATTERN = /^[a-z0-9-]+$/;

/** CONTRACT + producer reserved leftmost labels — not customer org keys. */
export const RESERVED_ORG_HOST_LABELS = new Set([
  "www",
  "api",
  "admin",
  "app",
  "login",
  "entra",
  "staging",
  "dev",
  "mail",
  "ftp",
  "localhost",
  "harvn",
]);

export type LocationLike = {
  protocol: string;
  hostname: string;
  port: string;
};

function readTrimmedEnv(value: string | undefined): string | undefined {
  if (!value?.trim()) {
    return undefined;
  }
  return value.trim();
}

/** Avoid `import.meta` so Jest can parse this module (see bootstrapImportMeta). */
function getViteEnvVar(name: string): string | undefined {
  if (typeof process !== "undefined" && process.env.NODE_ENV === "test") {
    return readTrimmedEnv(process.env[name]);
  }
  const env = (
    globalThis as { importMeta?: { env?: Record<string, string | undefined> } }
  ).importMeta?.env;
  return readTrimmedEnv(env?.[name]);
}

/**
 * Client cutover flag. Default **false** (EPIC-005 path routing).
 * Mirrors producer `ENTRA_SPA_ORG_SUBDOMAIN_HOST`.
 */
export function isOrgSubdomainHostEnabled(): boolean {
  const raw = getViteEnvVar("VITE_ENTRA_SPA_ORG_SUBDOMAIN_HOST");
  if (raw == null) {
    return false;
  }
  const normalized = raw.toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
}

/** SPA apex hostname for this build (`dev.harvn.ai`, `localhost`, …). */
export function getSpaApex(): string {
  return (getViteEnvVar("VITE_SPA_APEX") ?? "localhost").toLowerCase();
}

export function isReservedOrgHostLabel(label: string): boolean {
  return RESERVED_ORG_HOST_LABELS.has(label.toLowerCase());
}

/**
 * Normalize an org key to a valid SSO subdomain label, or null if reserved/invalid.
 * Used for hostname resolution and legacy redirects (open-redirect safe).
 */
export function normalizeSsoOrgLabel(
  label: string | null | undefined
): string | null {
  if (label == null) {
    return null;
  }
  const normalized = label.trim().toLowerCase();
  if (normalized === "") {
    return null;
  }
  if (isReservedOrgHostLabel(normalized)) {
    return null;
  }
  if (!SSO_LABEL_PATTERN.test(normalized)) {
    return null;
  }
  return normalized;
}

/**
 * Resolve org key from hostname leftmost label when host is `{label}.{spaApex}`
 * or `{label}.localhost`. Bare apex / reserved / invalid → null.
 */
export function resolveOrgKeyFromHostname(hostname: string): string | null {
  const host = hostname.trim().toLowerCase();
  if (host === "") {
    return null;
  }

  const apex = getSpaApex();

  if (host === apex || host === "localhost") {
    return null;
  }

  if (host.endsWith(".localhost")) {
    const parts = host.split(".");
    if (parts.length === 2 && parts[1] === "localhost") {
      return normalizeSsoOrgLabel(parts[0]);
    }
    return null;
  }

  const apexSuffix = `.${apex}`;
  if (host.endsWith(apexSuffix)) {
    const prefix = host.slice(0, -apexSuffix.length);
    if (prefix === "" || prefix.includes(".")) {
      return null;
    }
    return normalizeSsoOrgLabel(prefix);
  }

  return null;
}

/**
 * Build absolute URL on the org subdomain for legacy redirects.
 * Only composes `{org}.{apex}` or `{org}.localhost` — never trusts arbitrary hosts.
 */
export function buildOrgSubdomainAbsoluteUrl(
  orgKey: string,
  pathWithQuery: string,
  locationLike: LocationLike
): string {
  const org = normalizeSsoOrgLabel(orgKey);
  if (org === null) {
    throw new Error("INVALID_ORG_SUBDOMAIN_LABEL");
  }

  const apex = getSpaApex();
  const path = pathWithQuery.startsWith("/")
    ? pathWithQuery
    : `/${pathWithQuery}`;

  const useLocalhost =
    apex === "localhost" ||
    locationLike.hostname === "localhost" ||
    locationLike.hostname.toLowerCase().endsWith(".localhost");

  const host = useLocalhost ? `${org}.localhost` : `${org}.${apex}`;
  const port = locationLike.port ? `:${locationLike.port}` : "";
  const protocol = locationLike.protocol.endsWith(":")
    ? locationLike.protocol
    : `${locationLike.protocol}:`;

  return `${protocol}//${host}${port}${path}`;
}

/**
 * Map apex `/login/{orgKey}/…` pathname to org-host path under `/login/…`.
 */
export function mapLegacyOrgPathToHostPath(pathname: string): string {
  const match = pathname.match(/^\/login\/[^/]+(\/.*)?$/);
  if (!match) {
    return "/login/";
  }
  const suffix = match[1];
  if (suffix == null || suffix === "" || suffix === "/") {
    return "/login/";
  }
  return `/login${suffix}`;
}
